import { useCallback } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  createWorkflow,
  updateWorkflow,
} from "../../../../services/apiWorkflows";
import useMutate from "../../../../hooks/useMutate";
import { workflowSchema } from "../../../../schemas/createWorkFlow";
import {
  buildWorkflowPayload,
  createEmptyWorkflowStep,
  getWorkflowInitialValues,
} from "../utils/workflowAdminUtils";

export function useCreateWorkflowForm({
  close,
  workflowData = null,
  edit = false,
  workflowId = "",
}) {
  const formMethods = useForm({
    resolver: yupResolver(workflowSchema),
    defaultValues: getWorkflowInitialValues(workflowData),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = formMethods;

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "workflow_steps",
  });

  const { mutate: createWorkflowMutate } = useMutate(
    createWorkflow,
    ["adminWorkflow"],
    () => {},
    () => {
      close(false);
    }
  );

  const { mutate: updateWorkflowMutate } = useMutate(
    updateWorkflow,
    ["adminWorkflow"],
    () => {},
    () => {
      close(false);
    }
  );

  const handleOnDragEnd = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }

      move(result.source.index, result.destination.index);
    },
    [move]
  );

  const handleAddStep = useCallback(() => {
    append(createEmptyWorkflowStep());
    clearErrors("workflow_steps");
  }, [append, clearErrors]);

  const onSubmit = useCallback(
    (data) => {
      const { workflow_name: workflowName, workflow_steps: workflowSteps } = data;

      if (workflowSteps.length === 0) {
        setError("workflow_steps", {
          type: "manual",
          message: "At least one workflow step is required.",
        });
        return;
      }

      const payload = buildWorkflowPayload(workflowName, workflowSteps, edit);

      if (edit) {
        updateWorkflowMutate({ workflow_id: workflowId, data: payload });
        return;
      }

      createWorkflowMutate(payload);
    },
    [createWorkflowMutate, edit, setError, updateWorkflowMutate, workflowId]
  );

  return {
    ...formMethods,
    control,
    handleSubmit,
    errors,
    setValue,
    fields,
    remove,
    handleOnDragEnd,
    handleAddStep,
    onSubmit,
  };
}
